import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { SimpleCalendar } from 'public/components/Calendar';
import { useClassName, useWindowSize } from 'common/hooks';
import { ClockIcon, ChevronDownIcon } from 'public/icons';
import { Dropdown } from 'public/components/Dropdown';
import { BottomSheet } from 'public/components/BottomSheet';
import {
  formatHoursRange,
  getOpeningHoursForDate,
  isClosedHours,
} from 'utils/helpers/openingHours';

import './hours.less';

function RegularHours({ venue }) {
  const classNames = useClassName('RestaurantHours');
  return (
    <div className={classNames('regular')}>
      {venue.openingHours.regular.map((regular) => (
        <div key={regular.weekday}>
          <span>
            {regular.weekday.substring(0, 1).toUpperCase()}
            {regular.weekday.substring(1, 3)}
          </span>
          <span>
            {isClosedHours(regular)
              ? 'Closed'
              : formatHoursRange(regular, new Date())}
          </span>
        </div>
      ))}
    </div>
  );
}

export function VenueHours({ venue }) {
  const classNames = useClassName('RestaurantHours');
  const [isExpanded, setIsExpanded] = useState(false);
  const { isMobile } = useWindowSize();

  if (!venue?.openingHours) return null;
  const todayHours = getOpeningHoursForDate(venue.openingHours, new Date());
  const isClosed =
    venue.openingHours.temporarilyClosed || isClosedHours(todayHours);

  return (
    <div className={classNames('wrapper')}>
      <ClockIcon />
      <div className={classNames(['container', isClosed && 'closed'])}>
        <div>
          <span
            className={classNames(['label', isClosed ? 'closed' : 'opened'])}>
            {isClosed ? 'Closed' : 'Open'}
          </span>
        </div>
        {!isClosed && <span>&#8226;</span>}
        <div>
          {!isClosed && (
            <span className={classNames('label')}>
              {formatHoursRange(todayHours, new Date())}
            </span>
          )}
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className={classNames('arrow')}>
            <ChevronDownIcon />
          </div>
        </div>
        {isMobile ? (
          <BottomSheet
            isOpen={isExpanded}
            onClose={() => setIsExpanded(!isExpanded)}
            title="Hours of Operation"
            content={<RegularHours venue={venue} />}
          />
        ) : (
          <Dropdown
            isOpen={isExpanded}
            onClose={() => setIsExpanded(!isExpanded)}
            content={<RegularHours venue={venue} />}
          />
        )}
      </div>
    </div>
  );
}

function RegularHoursWithCustomDate({
  venue,
  selectedDate,
  setSelectedDate,
  onApply,
  currentView,
  targetView,
  setCurrentView,
  setTargetView,
  isMobile,
}) {
  const classNames = useClassName('HoursWithCustomDate');
  const [isFading, setIsFading] = useState(false);
  const transitionDuration = 300;

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(new Date(date));
    }
  };

  const switchView = (newView) => {
    if (newView !== currentView) {
      setTargetView(newView);
      setIsFading(true);

      if (newView === 'thisWeek') {
        setSelectedDate(new Date());
      }
    }
  };

  useEffect(() => {
    if (isFading) {
      const timeout = setTimeout(() => {
        setCurrentView(targetView);
        setIsFading(false);
      }, transitionDuration);
      return () => clearTimeout(timeout);
    }
  }, [isFading, targetView]);

  const isDateToday =
    new Date().toISOString().split('T')[0] ===
    selectedDate.toISOString().split('T')[0];

  return (
    <div className={classNames('container')}>
      <div className={classNames('header')}>Hours of Operations</div>
      <div className={classNames('content')}>
        <div className={classNames('buttons')}>
          <button
            className={classNames(
              'button',
              currentView === 'thisWeek' && 'active'
            )}
            onClick={() => switchView('thisWeek')}>
            This Week
          </button>
          <button
            className={classNames(
              'button',
              currentView === 'customDate' && 'active'
            )}
            onClick={() => switchView('customDate')}>
            Custom Date
          </button>
        </div>
        <div
          className={classNames(
            'hours-and-picker',
            isFading ? 'fade-out' : 'fade-in'
          )}
          style={{ transition: `opacity ${transitionDuration}ms ease-in-out` }}>
          {currentView === 'thisWeek' ? (
            <div className={classNames('regular')}>
              {venue.openingHours.regular.map((regular) => (
                <div key={regular.weekday}>
                  <span>
                    {regular.weekday.substring(0, 1).toUpperCase()}
                    {regular.weekday.substring(1)}
                  </span>
                  <span>
                    {isClosedHours(regular)
                      ? 'Closed'
                      : formatHoursRange(regular, selectedDate)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <SimpleCalendar
              initialDate={selectedDate.toISOString()}
              updateSelectedDate={handleDateSelect}
              isDateUnavailable={() => false}
            />
          )}
        </div>
      </div>
      {isMobile && (
        <div className={classNames('footer')}>
          <button
            className={classNames(
              currentView === 'customDate' ? 'apply-button' : 'close-button',
              !isDateToday && 'different-date'
            )}
            onClick={onApply}
            disabled={currentView === 'customDate' && isDateToday}>
            {currentView === 'customDate' ? 'Apply' : 'Close'}
          </button>
        </div>
      )}
    </div>
  );
}

export function VenueHoursCustom({ venue, theme, showSundayLink }) {
  const classNames = useClassName('RestaurantHours');
  const { isMobile } = useWindowSize();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('thisWeek');
  const [targetView, setTargetView] = useState('thisWeek');

  if (!venue?.openingHours) return null;

  const hoursForDisplay = getOpeningHoursForDate(
    venue.openingHours,
    selectedDate
  );
  const isClosed =
    venue.openingHours.temporarilyClosed || isClosedHours(hoursForDisplay);
  const isSelectedDateSunday = selectedDate.getDay() === 0;
  const shouldShowSundayLink =
    showSundayLink && isClosed && isSelectedDateSunday;
  const formattedDate = selectedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const content = (
    <RegularHoursWithCustomDate
      venue={venue}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      onApply={() => setIsExpanded(false)}
      currentView={currentView}
      targetView={targetView}
      setCurrentView={setCurrentView}
      setTargetView={setTargetView}
      isMobile={isMobile}
    />
  );

  return (
    <div className={classNames(['wrapper', theme])}>
      <ClockIcon {...(theme && { className: classNames(`fill-${theme}`) })} />
      <div className={classNames('container')}>
        {shouldShowSundayLink ? (
          <Link className={classNames('sunday-message')} to="/sundays">
            See Stores Open on Sunday
          </Link>
        ) : (
          <>
            <span
              className={classNames([
                'label',
                isClosed ? 'closed' : 'opened',
                theme,
              ])}>
              {isClosed ? 'Closed' : 'Open'}
            </span>
            <span>&#8226;</span>
          </>
        )}
        <div>
          {!shouldShowSundayLink && (
            <span className={classNames('date')}>
              {!isClosed ? `${formattedDate},` : formattedDate}
            </span>
          )}
          {!isClosed && (
            <span className={classNames('hours-label')}>
              {formatHoursRange(hoursForDisplay, selectedDate)}
            </span>
          )}
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className={classNames('arrow')}>
            <ChevronDownIcon
              {...(theme && { className: classNames(`stroke-${theme}`) })}
            />
          </div>
        </div>
        {isMobile ? (
          <BottomSheet
            isOpen={isExpanded}
            onClose={() => setIsExpanded(!isExpanded)}
            title="Hours of Operation"
            content={content}
          />
        ) : (
          <Dropdown
            isOpen={isExpanded}
            onClose={() => setIsExpanded(!isExpanded)}
            content={content}
            watchEdges={true}
          />
        )}
      </div>
    </div>
  );
}
